import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import axios from 'axios';
import { REST_API_BASE_URL } from '../config';

interface AuthContextValue {
  role: string | null;
  isLoading: boolean;
  setRole: (role: string | null) => void;
  clearRole: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  role: null,
  isLoading: true,
  setRole: () => {},
  clearRole: () => {},
});

export const AuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  // Seed from localStorage so the first render is not blank.
  // The server response on mount is authoritative and will overwrite this.
  const [role, setRoleState] = useState<string | null>(() =>
    localStorage.getItem('role'),
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');

    // Skip the network round-trip when there is no token — the 403 is guaranteed.
    if (!token) {
      setRoleState(null);
      localStorage.removeItem('role');
      setIsLoading(false);
      return;
    }

    axios
      .get<{ role: string }>(`${REST_API_BASE_URL}/me`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        const serverRole = res.data.role ?? null;
        setRoleState(serverRole);
        // Keep localStorage in sync so the seed is accurate on next page load
        if (serverRole) {
          localStorage.setItem('role', serverRole);
        } else {
          localStorage.removeItem('role');
        }
      })
      .catch(() => {
        // Token present but rejected (expired/tampered) — clear all stale auth state
        setRoleState(null);
        localStorage.removeItem('role');
        localStorage.removeItem('token');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const setRole = useCallback((r: string | null) => {
    setRoleState(r);
    if (r) {
      localStorage.setItem('role', r);
    } else {
      localStorage.removeItem('role');
    }
  }, []);

  const clearRole = useCallback(() => {
    setRoleState(null);
    localStorage.removeItem('role');
  }, []);

  return (
    <AuthContext.Provider value={{ role, isLoading, setRole, clearRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValue => useContext(AuthContext);
